/**
 * Lightweight performance monitoring.
 * Reports Web Vitals and slow navigation to audit_log.
 */
import { reportError } from "@/lib/errorReporting";

/** Track slow page navigations (> threshold) */
const SLOW_NAV_THRESHOLD_MS = 3000;

let navigationStart = 0;

export function markNavigationStart() {
  navigationStart = performance.now();
}

export function markNavigationEnd(pagePath: string) {
  if (!navigationStart) return;
  const duration = performance.now() - navigationStart;
  navigationStart = 0;

  if (duration > SLOW_NAV_THRESHOLD_MS) {
    reportError("medium", `Slow page load: ${Math.round(duration)}ms`, {
      feature: "performance",
      detail: `path=${pagePath} duration=${Math.round(duration)}ms`,
    });
  }
}

/** Observe Web Vitals using PerformanceObserver */
export function observeWebVitals() {
  if (typeof PerformanceObserver === "undefined") return;

  // Largest Contentful Paint
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as any;
      if (last && last.startTime > 4000) {
        reportError("medium", `Poor LCP: ${Math.round(last.startTime)}ms`, {
          feature: "web_vitals",
          detail: `element=${last.element?.tagName || "unknown"}`,
        });
      }
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
  } catch { /* not supported */ }

  // Long Tasks (>50ms)
  try {
    let longTaskCount = 0;
    const ltObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 200) {
          longTaskCount++;
          if (longTaskCount <= 3) {
            reportError("low", `Long task: ${Math.round(entry.duration)}ms`, {
              feature: "performance",
              detail: `name=${entry.name}`,
            });
          }
        }
      }
    });
    ltObserver.observe({ type: "longtask", buffered: true });
  } catch { /* not supported */ }

  // Cumulative Layout Shift
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      }
      if (clsValue > 0.25) {
        reportError("medium", `High CLS: ${clsValue.toFixed(3)}`, {
          feature: "web_vitals",
        });
      }
    });
    clsObserver.observe({ type: "layout-shift", buffered: true });
  } catch { /* not supported */ }
}
